import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useDemoScenarios } from "@/hooks/useDemoScenarios";
import { ClinicalCodeSelector } from "./ClinicalCodeSelector";
import type { ClinicalCodeWithOrder, DemoScenarioWithFormattedCodes } from "@/types/demo";
import { Loader2 } from "lucide-react";
import { log } from "@/utils/debug";
import { useNavigate } from "react-router";

interface EditDemoScenarioFormProps {
  scenarioId: string;
  initialData?: DemoScenarioWithFormattedCodes;
}

export function EditDemoScenarioForm({ scenarioId, initialData }: EditDemoScenarioFormProps) {
  const [demoText, setDemoText] = useState("");
  const [selectedCodes, setSelectedCodes] = useState<ClinicalCodeWithOrder[]>([]);
  const { updateScenario } = useDemoScenarios();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (initialData) {
      setDemoText(initialData.demo_text);
      setSelectedCodes(initialData.clinical_codes as unknown as ClinicalCodeWithOrder[]);
      log.process("Loaded initial data for editing", {
        id: initialData.id,
        textLength: initialData.demo_text.length,
        lineCount: initialData.demo_text.split("\n").length,
        codesCount: (initialData.clinical_codes as any[]).length,
      });
    } else {
      // Try to get from session storage
      const storedData = sessionStorage.getItem("editScenario");
      if (storedData) {
        const parsedData = JSON.parse(storedData) as DemoScenarioWithFormattedCodes;
        if (parsedData.id === scenarioId) {
          setDemoText(parsedData.demo_text);
          setSelectedCodes(parsedData.clinical_codes as unknown as ClinicalCodeWithOrder[]);
          log.process("Loaded scenario data from session storage", {
            id: parsedData.id,
            textLength: parsedData.demo_text.length,
            lineCount: parsedData.demo_text.split("\n").length,
            codesCount: (parsedData.clinical_codes as any[]).length,
          });
        }
      }
    }
  }, [initialData, scenarioId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!demoText.trim()) {
      toast.error("Please enter demo text");
      return;
    }

    if (selectedCodes.length === 0) {
      toast.error("Please select at least one clinical code");
      return;
    }

    setIsSubmitting(true);
    try {
      // Use raw text directly
      log.process("Updating scenario with raw text:", {
        textLength: demoText.length,
        lineCount: demoText.split("\n").length,
        textSample: demoText.substring(0, 50) + (demoText.length > 50 ? "..." : ""),
      });

      await updateScenario.mutateAsync({
        id: scenarioId,
        demo_text: demoText, // Use raw text
        clinical_codes: selectedCodes as unknown as any,
      });

      toast.success("Demo scenario updated successfully");
      // Clear the stored scenario
      sessionStorage.removeItem("editScenario");
      // Navigate back to the demo management page
      navigate("/admin/demo-management");
    } catch (_error) {
      toast.error("Failed to update demo scenario");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/demo-management");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="demoText" className="text-sm font-medium">
          Demo Text
        </label>
        <Textarea
          id="demoText"
          value={demoText}
          onChange={(e) => setDemoText(e.target.value)}
          placeholder="Enter the demo text..."
          className="min-h-[150px]"
          disabled={isSubmitting}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Clinical Codes</label>
        <ClinicalCodeSelector value={selectedCodes} onChange={setSelectedCodes} disabled={isSubmitting} />
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating...
            </>
          ) : (
            "Update Demo Scenario"
          )}
        </Button>
      </div>
    </form>
  );
}
